import { memo } from 'react';

import { CurrencyField, NumberField } from '@myn/mui';

import {
  BUILDER_ROW_TYPES,
  DECIMAL_LIMIT,
} from '../../../../../../constants/campaignBuilder.js';
import { Columns } from '../../../../../../constants/myCampaigns.js';
import { BuilderTableColumn, BuilderTableRow } from '../../../../types.js';
import FormattedField from '../FormattedField/FormattedField.jsx';
import useEditableNumericField from '../hooks/useEditableNumericField.js';
import TableEditCell from '../TableEditCell/TableEditCell.jsx';
import InputWrapper from '../wrappers/FormattingInputWrapper.jsx';

type Props = {
  value: string | null;
  column: BuilderTableColumn;
  row: BuilderTableRow;
  cellColor: string;
};

const CurrencyFieldEditable = memo(
  ({ value, column, row, cellColor }: Props) => {
    const decimalLimit = column.decimals ?? DECIMAL_LIMIT.BUDGET;
    const {
      currencyValue: currency,
      type,
      scale,
      canVatBeMadeCalculated,
      canAdserverCostBeMadeCalculated,
    } = row.original;
    const isTileRow = type === BUILDER_ROW_TYPES.TILE;

    const {
      isFieldFocused,
      handleFieldFocus,
      handleFieldBlur,
      isReadOnlyField,
      numberValue,
      handleValueChange,
      handleValueReset,
    } = useEditableNumericField({
      value,
      column,
      row,
    });

    const getIsResetBtnShown = () => {
      if (column.isCalculatedTechCostColumn) {
        return !!numberValue && !isFieldFocused;
      }

      const isResetBtnAllowed =
        (!!numberValue || numberValue === 0) && !isFieldFocused;

      switch (column.id) {
        case Columns.VAT: {
          return canVatBeMadeCalculated && isResetBtnAllowed;
        }
        case Columns.ADSERVER_COST: {
          return canAdserverCostBeMadeCalculated && isResetBtnAllowed;
        }
        case Columns.BUDGET: {
          return false;
        }
        default: {
          return isResetBtnAllowed;
        }
      }
    };

    const isResetBtnShown = getIsResetBtnShown();

    // hide zero value for every row but the media row level
    const isEmptyCurrencyValueHidden =
      !Number(value) && !!column.hideCurrencyForEmptyValue && !isTileRow;

    if (isReadOnlyField) {
      return (
        <FormattedField
          scale={scale}
          currency={currency}
          value={value}
          decimalLimit={decimalLimit}
          isEmptyValueHidden={isEmptyCurrencyValueHidden}
          color={cellColor}
          variant="currency"
        />
      );
    }

    if (!isFieldFocused) {
      return (
        <TableEditCell
          scale={scale}
          isDeleteButtonShown={isResetBtnShown}
          deleteValueFunc={handleValueReset}
          showEditModeFunc={handleFieldFocus}
          startMeasurePerformance={column.startMeasurePerformance}
          currency={currency}
          value={numberValue}
          decimalLimit={decimalLimit}
          variant="currency"
        />
      );
    }

    if (currency) {
      return (
        <InputWrapper $scale={scale}>
          <CurrencyField
            currency={currency}
            value={numberValue}
            onBlur={handleFieldBlur}
            onChange={handleValueChange}
            autoFocus
            maxDecimals={decimalLimit}
            restFormatProps={{
              min: 0,
              minDecimals: decimalLimit,
            }}
          />
        </InputWrapper>
      );
    }

    return (
      <InputWrapper $scale={scale}>
        <NumberField
          value={numberValue}
          onBlur={handleFieldBlur}
          onChange={handleValueChange}
          autoFocus
          maxDecimals={decimalLimit}
          restFormatProps={{
            min: 0,
            minDecimals: decimalLimit,
          }}
        />
      </InputWrapper>
    );
  },
);

export default CurrencyFieldEditable;
